import { motion } from "framer-motion";
import { MapPin, Navigation, Phone, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n";
import { PHONES, ADDRESS, MAPS_URL, waLink } from "@/lib/site";

const reveal = {
    initial: { opacity: 0, y: 26 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
};

export default function ContactSection() {
    const { t } = useLang();
    const c = t.contact;

    return (
        <section id="contact" className="scroll-mt-24 py-16 sm:py-24">
            <div className="mx-auto max-w-3xl px-4 sm:px-6">
                <motion.div {...reveal} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="text-center">
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand">{c.eyebrow}</p>
                    <h2 className="font-display mt-2 text-3xl font-bold tracking-tight text-ink sm:text-4xl">
                        {c.title}
                    </h2>
                </motion.div>

                <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {PHONES.map((p, i) => (
                        <motion.div
                            key={p.tel}
                            {...reveal}
                            transition={{ duration: 0.6, delay: 0.06 * i, ease: [0.16, 1, 0.3, 1] }}
                            className="rounded-3xl border border-emerald-900/10 bg-white p-5 shadow-sm sm:p-6"
                            data-testid={`contact-phone-card-${i + 1}`}
                        >
                            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                {c.phoneLabel} {i + 1}
                            </p>
                            <p className="font-display mt-1 text-xl font-bold tracking-tight text-ink sm:text-2xl" dir="ltr">
                                {p.label}
                            </p>
                            <div className="mt-4 grid grid-cols-2 gap-2.5">
                                <a
                                    href={`tel:${p.tel}`}
                                    data-testid={`contact-call-button-${i + 1}`}
                                    className="flex h-11 items-center justify-center gap-1.5 rounded-full bg-brand text-sm font-semibold text-white transition-colors hover:bg-brand-dark"
                                >
                                    <Phone className="h-4 w-4" />
                                    {c.call}
                                </a>
                                <a
                                    href={waLink(p.wa, "Hello Azmthullah Packers & Movers! I need a moving quote.")}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`contact-whatsapp-button-${i + 1}`}
                                    className="flex h-11 items-center justify-center gap-1.5 rounded-full bg-[#25D366] text-sm font-semibold text-white transition-all hover:brightness-95"
                                >
                                    <MessageCircle className="h-4 w-4" />
                                    {c.wa}
                                </a>
                            </div>
                        </motion.div>
                    ))}
                </div>
                <p className="mt-3 text-center text-xs text-slate-500">{c.waNote}</p>

                <motion.div
                    {...reveal}
                    transition={{ duration: 0.6, delay: 0.12 }}
                    className="mt-6 rounded-3xl border border-emerald-900/10 bg-ink p-6 text-white sm:p-8"
                >
                    <div className="flex items-start gap-4">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-gold">
                            <MapPin className="h-5 w-5" />
                        </span>
                        <div className="min-w-0">
                            <p className="text-xs font-bold uppercase tracking-wider text-white/60">{c.addressLabel}</p>
                            <p className="mt-1 text-sm leading-relaxed text-white/90 sm:text-base">{ADDRESS}</p>
                            <a
                                href={MAPS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-directions-link"
                                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-gold px-4 py-2 text-sm font-bold text-ink transition-transform hover:-translate-y-0.5"
                            >
                                <Navigation className="h-4 w-4" />
                                {c.directions}
                            </a>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
